import fetch from 'lib/decorated-fetch'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import useOptimisticStatus, {
  useStatusCountUpdater,
} from 'Components/hooks/use-optimistic-status'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { GlobalStoreContext } from 'Components/store'
import useLoginCheckCallback from 'Components/hooks/use-login-check-callback'
import { aid, appName } from 'Constants/index'
import { DiggBuryContext } from 'Components/new/community-card/interact'
import { logger, metrics } from 'lib/slardar-utils'

export interface ICollectParams {
  group_id?: string
  user_id?: string
}

export type DiggType =
  | 'digg_thread'
  | 'digg_ugc_video'
  | 'digg_pgc_article'
  | 'digg_pgc_video'
  | 'digg_car_review'
  | 'digg_essay'

export interface IToggleDiggParams {
  type?: 'community' | 'comment' | 'reply' // 帖子 / 评论 / 回复
  id?: string // 主id
  groupId?: string // 帖子id
  commentId?: string // 评论id
  hasDigg?: boolean // 是否已点赞
  diggCount?: number // 点赞数
  cellStyle?: number // 帖子cellStyle，hooks内部根据cellStyle会自动映射点赞类型
  articleType?: number // 文章articleType，hooks内部根据articleType会自动映射点赞类型
}

export function makeMap<T>(raw: [string, T][]) {
  return raw.reduce((map, [cellStyles, diggType]) => {
    cellStyles.split(',').forEach((cellStyle) => {
      map[cellStyle] = diggType
    })
    return map
  }, {} as Record<string, T>)
}

// cell_style 与点赞类型的映射关系
export const cellStyleDiggTypeMap = makeMap<DiggType>([
  [
    '5309,1155,5619,5620,5621,5622,5623,8999,8998,8006,8005,8004,8007,8001,8003,8002,2316,2314,2362,2318,2328,2341',
    'digg_thread',
  ],
  ['2363,2317,2307,2313', 'digg_ugc_video'],
  ['2000,2001,2002,2003,2008,2050,2051', 'digg_pgc_article'],
  ['2101,2100,2052,2053', 'digg_pgc_video'],
  ['2061,2062', 'digg_car_review'],
  ['2330,2332,2364', 'digg_essay'],
])

// articleType:
// 0，未知，老口碑；
// 1，pgc 图文；
// 2，pgc 视频（不含图文嵌视频）；
// 3，pgc 图文嵌视频；
// 4，pgc 图集（当前合并到图文了）；
// 5，ugc 文字；
// 6，ugc 小视频；
// 7，ugc 图文；
// 8，ugc 问答；
// 9，UGC 长贴；
// 10，ugc 投票；
// 11，ugc 转发；
// 12，ugc 长贴口碑；
// 13，ugc 口碑
// 14，懂车分
export const articleTypeDiggTypeMap = makeMap([
  ['1,3,4', 'digg_pgc_article'],
  ['2', 'digg_pgc_video'],
  ['6', 'digg_ugc_video'],
  ['14', 'digg_car_review'],
  ['9', 'digg_essay'],
  ['5,7,8,10,11,12,13', 'digg_thread'],
])

function useToggleDigg({
  id,
  type = 'community',
  hasDigg,
  diggCount,
  groupId,
  commentId,
  cellStyle,
  articleType,
}: IToggleDiggParams) {
  const { state } = useContext(GlobalStoreContext)
  const userId = state?.userData?.user_id

  const { begin, rollback, curStatus, curCount } = useStatusCountUpdater({
    status: hasDigg,
    count: diggCount,
  })

  const { actionsRef } = useContext(DiggBuryContext)

  useEffect(() => {
    if (actionsRef) {
      actionsRef.current.diggBegin = begin
    }
  }, [actionsRef, begin])

  useEffect(() => {
    if (actionsRef) {
      actionsRef.current.diggRollback = rollback
    }
  }, [actionsRef, rollback])

  useEffect(() => {
    if (actionsRef) {
      actionsRef.current.diggStatus = curStatus
    }
  }, [actionsRef, curStatus])

  const buryPrevRef =
    useRef<
      ReturnType<ReturnType<typeof useStatusCountUpdater>['begin']>['prev']
    >()

  return {
    update: useOptimisticStatus<[boolean, number]>({
      execute: useLoginCheckCallback(
        useCallback(
          ([curStatus]) => {
            // 评论点赞
            if (type === 'comment') {
              return fetch(
                '/motor/proxy/comment_action/browser/2/data/v2/comment_action/',
                {
                  method: 'POST',
                  headers: {
                    'tt-anti-token':
                      window.localStorage.getItem('tt-anti-token') || '',
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: serializeObject({
                    aid,
                    app_name: appName,
                    comment_id: id,
                    item_id: groupId, //文章id
                    group_id: groupId, //文章id
                    action: curStatus ? 'digg' : 'cancel_digg',
                  }),
                }
              ).then((res) => res?.errno === 0)
            } else if (type === 'reply') {
              return fetch(
                '/motor/proxy/digg_reply/browser/2/comment/v2/digg_reply/',
                {
                  method: 'POST',
                  headers: {
                    'tt-anti-token':
                      window.localStorage.getItem('tt-anti-token') || '',
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: serializeObject({
                    aid,
                    app_name: appName,
                    id: commentId, // 评论id
                    reply_id: id, // 回复id
                    item_id: groupId,
                    group_id: groupId,
                    action: curStatus ? 'digg' : 'cancel_digg',
                  }),
                }
              ).then((res) => res?.errno === 0)
            } else {
              // 帖子点赞
              return fetch(`/motor/community/digg_like`, {
                method: 'POST',
                headers: {
                  'tt-anti-token':
                    window.localStorage.getItem('tt-anti-token') || '',
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: serializeObject({
                  aid,
                  // user_id: userId,
                  thread_id: id,
                  group_id: id,
                  digg_action: curStatus ? 'digg' : 'cancel_digg',
                  like_type:
                    articleTypeDiggTypeMap[articleType!] ||
                    cellStyleDiggTypeMap[cellStyle!] ||
                    'digg_thread',
                }),
              }).then((res) => {
                if (res?.status !== 0) {
                  metrics.counter('digg_like.fail')
                }
                return res?.status === 0
              })
            }
          },
          [userId, type, id, groupId, commentId, cellStyle]
        ),
        useMemo(
          () => ({
            enter_method: 'like',
          }),
          []
        )
      ),
      begin: useCallback(() => {
        if (actionsRef) {
          const { buryBegin, buryStatus } = actionsRef.current
          if (buryBegin && !curStatus && !buryStatus !== !curStatus) {
            buryPrevRef.current = buryBegin().prev
          }
        }
        return begin()
      }, [actionsRef, curStatus, begin]),
      rollback: useCallback(
        ([curStatus, curCount]) => {
          if (actionsRef) {
            const { buryRollback, buryStatus } = actionsRef.current
            if (buryRollback && !curStatus && !buryStatus !== !curStatus) {
              buryRollback(buryPrevRef.current)
            }
          }
          return rollback([curStatus, curCount])
        },
        [actionsRef, rollback]
      ),
    }),
    curStatus,
    curCount,
  }
}

export default useToggleDigg
